import { UseQueryResult } from '@tanstack/react-query';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import {
  BulkListingPriceCalculationResult,
  CompListing,
  Event,
  Listing,
  ListingGroup,
  ListingPriceCalculation,
  SectionInfo,
  SectionRowScoreRecommendations,
} from 'src/WebApiController';
import { useContextSelector } from 'use-context-selector';

import {
  IInventoryEventDetailContext,
  InventoryEventDetailContextWithSelector,
} from './InventoryEventDetailContext';

const useInventoryEventDetailContextValueSelector = <T>(
  selector: (context: IInventoryEventDetailContext) => T
): T => {
  const contextValue = useContextSelector(
    InventoryEventDetailContextWithSelector,
    selector
  );

  return contextValue;
};

export const useInventoryEventDetailContextEventSelector = ():
  | Event
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.event
  );
};

export const useInventoryEventDetailContextEventListingsSelector = ():
  | Listing[]
  | null
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.eventListings
  );
};

export const useInventoryEventDetailContextSelectedListingIdsSelector =
  (): number[] => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.selectedListingIds
    );
  };

export const useInventoryEventDetailContextSelectedListingSelector = ():
  | Listing
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.selectedListing
  );
};

export const useInventoryEventDetailContextSelectedGroupSelector = ():
  | ListingGroup
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.selectedGroup
  );
};

export const useInventoryEventDetailContextFlattenedListingGroupSelector = ():
  | Listing[]
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.flattenedListingGroup
  );
};

export const useInventoryEventDetailContextCompQuantityFiltersSelector =
  (): readonly [string[], React.Dispatch<React.SetStateAction<string[]>>] => {
    const compQuantityFilters = useInventoryEventDetailContextValueSelector(
      (context) => context.compQuantityFilters
    );
    const setCompQuantityFilters = useInventoryEventDetailContextValueSelector(
      (context) => context.setCompQuantityFilters
    );
    return [compQuantityFilters, setCompQuantityFilters];
  };

export const useInventoryEventDetailContextPriceCalcQuerySelector =
  (): UseQueryResult<ListingPriceCalculation | null, Error> | null => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.priceCalcQuery
    );
  };

export const useInventoryEventDetailContextListingGroupPriceCalcQuerySelector =
  (): UseQueryResult<
    BulkListingPriceCalculationResult | null,
    Error
  > | null => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.listingGroupPriceCalcQuery
    );
  };

export const useInventoryEventDetailContextCompListingsSelector = ():
  | CompListing[]
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.compListings
  );
};

export const useInventoryEventDetailContextFilterSectionsSelector =
  (): SectionInfo[] => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.filterSections
    );
  };

export const useInventoryEventDetailContextCompListingSectionIdsSelector =
  (): number[] => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.compListingSectionIds
    );
  };

export const useInventoryEventDetailContextGetColorSelector = (): NonNullable<
  VenueMapContentProps['getColor']
> => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.getColor
  );
};

export const useInventoryEventDetailContextSplitChoicesSelector = (): Record<
  string,
  string
> => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.splitChoices
  );
};

export const useInventoryEventDetailContextIsLoadingSelector = (): boolean => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.isLoading
  );
};

export const useInventoryEventDetailContextSectionRowScoreRecommendationsSelector =
  (): SectionRowScoreRecommendations | undefined => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.sectionRowScoreRecommendations
    );
  };

export const useInventoryEventDetailContextIsRecommendationDeclinedSelector =
  (): boolean => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.isRecommendationDeclined
    );
  };

export const useInventoryEventDetailContextOnDeclineRecommendationSelector =
  (): (() => void) => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.onDeclineRecommendation
    );
  };

export const useInventoryEventDetailContextRecommendingSectionIdsSelector = ():
  | number[]
  | undefined => {
  return useInventoryEventDetailContextValueSelector(
    (context) => context.recommendingSectionIds
  );
};

export const useInventoryEventDetailContextOnToggleRecommendingSectionIdsSelector =
  (): ((
    sectionIds: number[],
    options?: { include?: boolean; exclude?: boolean }
  ) => void) => {
    return useInventoryEventDetailContextValueSelector(
      (context) => context.onToggleRecommendingSectionIds
    );
  };
